import type { QueryClient } from '@tanstack/react-query';
import { invalidateReleaseState, type InvalidateReleaseOptions } from '@/lib/invalidateRelease';

export const RELEASE_INVALIDATE_DEBOUNCE_MS = 500;

interface PendingReleaseInvalidation {
  queryClient: QueryClient;
  teamId: number | null;
  alsoTeamIds: Set<number>;
  postIds: Set<number>;
  hasBroadEvent: boolean;
  scope: 'full' | 'comment';
}

/**
 * Trailing debounce for SSE-triggered release-state invalidation. Event bursts
 * (bulk approvals, auto-translations) merge into one invalidation per window:
 * 'full' scope wins over 'comment'; one distinct postId stays post-specific,
 * several (or any event without a postId) fall back to broad approval invalidation.
 */
export function createReleaseInvalidationDebouncer(delayMs: number = RELEASE_INVALIDATE_DEBOUNCE_MS, broadcast: boolean = false): {
  schedule: (queryClient: QueryClient, opts: InvalidateReleaseOptions) => void;
  cancel: () => void;
} {
  let pending: PendingReleaseInvalidation | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = (): void => {
    timer = null;
    const batch = pending;
    pending = null;
    if (!batch) return;
    const postId =
      !batch.hasBroadEvent && batch.postIds.size === 1 ? [...batch.postIds][0] : undefined;
    invalidateReleaseState(batch.queryClient, {
      teamId: batch.teamId,
      alsoTeamIds: [...batch.alsoTeamIds],
      postId,
      scope: batch.scope,
      broadcast, // SSE / cross-tab receive: already reaches every tab, never echo
    });
  };

  return {
    schedule: (queryClient, opts) => {
      if (pending && (pending.teamId !== opts.teamId || pending.queryClient !== queryClient)) {
        if (timer) clearTimeout(timer);
        flush();
      }
      if (!pending) {
        pending = {
          queryClient,
          teamId: opts.teamId,
          alsoTeamIds: new Set<number>(),
          postIds: new Set<number>(),
          hasBroadEvent: false,
          scope: 'comment',
        };
      }
      for (const id of opts.alsoTeamIds ?? []) {
        if (typeof id === 'number') pending.alsoTeamIds.add(id);
      }
      if (opts.postId === undefined) pending.hasBroadEvent = true;
      else pending.postIds.add(opts.postId);
      if ((opts.scope ?? 'full') === 'full') pending.scope = 'full';
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, delayMs);
    },
    cancel: () => {
      if (timer) clearTimeout(timer);
      timer = null;
      pending = null;
    },
  };
}
